import React from 'react';
import { Typography } from 'antd';
import { MobileOutlined, EditOutlined } from '@ant-design/icons';

import MoneyTitle from '../customer/MoneyTitle';
import { colorForBackgroundColor, generateSelectedColor, getContrastWithRGBColor } from '../../utils/RGBColorToHex';
import { getFees, getPaymentAmounts } from '../../utils/feeCalculation';

const { Text, Title } = Typography;

const isDarkNearBlack = (color) => getContrastWithRGBColor(color) === 'dark' && !generateSelectedColor(color);

const calculatePercentage = (percent, total) => {
  const percentageFactor = percent / 10000;
  const baseAmount = Math.round(percentageFactor * total);
  return total > 0 ? Math.max(baseAmount, 100) : 0;
};

const getFieldMeta = (field) => {
  const fieldType = field?.type || field?.name;
  switch (fieldType) {
    case 'name':
      return { label: 'Your name', placeholder: 'Enter your name (private)' };
    case 'message':
      return { label: 'Message', placeholder: 'Send a private message' };
    case 'review':
      return { label: 'Review', placeholder: 'Leave a review' };
    case 'mobile':
      return { label: 'Phone number', placeholder: 'Enter your phone number' };
    case 'email':
      return { label: 'Email', placeholder: 'Enter your email' };
    default:
      return { label: field?.label || field?.name || 'Field', placeholder: field?.placeholder || 'Enter value' };
  }
};

const TipPagePreview = ({ team }) => {
  const {
    teamName,
    topLogo,
    rgbColor = '#FF0098',
    values = [],
    percents = [],
    selectedValueByIndex = 0,
    paymentDetails = {},
  } = team || {};

  const topBackgroundColor = rgbColor;
  const baseContrastColor = colorForBackgroundColor(topBackgroundColor);
  const selectionColor = isDarkNearBlack(topBackgroundColor) ? '#ff0098' : topBackgroundColor;
  const selectionContrastColor = colorForBackgroundColor(selectionColor);

  const currency = team?.defaultCurrency || paymentDetails.currency || 'EUR';
  const hasPercents = percents && percents.length > 0;
  const hasValues = values && values.length > 0;
  const usePercents = hasPercents;

  const displayItems = usePercents ? percents : (hasValues ? values : [500, 1000, 1500]);
  const safeIndex = Math.min(Math.max(selectedValueByIndex, 0), displayItems.length - 1);

  const fieldSettings = Array.isArray(paymentDetails.fieldSettings) ? paymentDetails.fieldSettings : [];
  const visibleFields = fieldSettings.filter((field) => field?.requirement !== 'hidden');
  const showFeesToggle = paymentDetails.shouldShowFeesOption ?? true;
  const includeFeesByDefault = paymentDetails.shouldAddOnFees ?? true;

  const appliedRevenueFeeSlab = paymentDetails.appliedRevenueFeeSlab
    || paymentDetails.revenueFee?.[paymentDetails.appliedRevenueFeeSlabId];

  const billAmount = 0;
  const selectedAmount = usePercents
    ? calculatePercentage(displayItems[safeIndex] || 0, billAmount)
    : (displayItems[safeIndex] || 0);

  const { feeTotal } = getPaymentAmounts(selectedAmount, includeFeesByDefault, appliedRevenueFeeSlab);

  return (
    <div style={{ 
      position: 'sticky', 
      top: '24px', 
      width: '100%', 
      maxWidth: '320px',
      margin: '0 auto' 
    }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '12px', 
        color: '#8c8c8c', 
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        fontWeight: 'bold'
      }}>
        <MobileOutlined /> Tip Page Preview
      </div>
      
      <div style={{ 
        background: '#000000', 
        borderRadius: '40px',
        border: '12px solid #1A1A1A',
        boxShadow: '0 30px 60px rgba(0,0,0,0.8)',
        overflowY: 'auto',
        minHeight: '560px',
        maxHeight: 'min(820px, 75vh)',
        aspectRatio: '9 / 19.5',
        width: '100%',
        maxWidth: '320px',
        margin: '0 auto',
        position: 'relative'
      }}>
        <div style={{ 
          background: topBackgroundColor, 
          padding: '32px 16px 20px', 
          textAlign: 'center',
          color: baseContrastColor
        }}>
          {topLogo && (
            <img 
              src={topLogo} 
              alt="Avatar" 
              style={{ width: '72px', height: '72px', borderRadius: '50%', marginBottom: '12px', objectFit: 'cover', background: 'white', padding: '2px' }} 
            />
          )}
          <div style={{ fontSize: '13px', opacity: 0.8 }}>Leave a tip for</div>
          <Title level={4} style={{ color: baseContrastColor, margin: '6px 0 0', fontSize: '20px', fontWeight: '700' }}>
            {teamName || 'Team Member'}
          </Title>
          <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
            (Front of House)
          </div>
        </div>

        <div style={{ padding: '20px 6px 40px' }}>
          {hasPercents && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
                Bill amount
              </div>
              <div style={{ 
                background: '#141414', 
                border: '1px solid #fff', 
                borderRadius: '8px', 
                padding: '12px',
                textAlign: 'center',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '56px'
              }}>
                <span style={{ fontSize: '32px', color: 'rgba(255,255,255,0.4)', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '22px', marginRight: '2px', marginTop: '4px' }}>{currency === 'EUR' ? '€' : (currency === 'GBP' ? '£' : '$')}</span>
                  0
                </span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {displayItems.map((val, idx) => (
              <div
                key={`option-${val}-${idx}`}
                style={{
                  flex: 1,
                  height: '44px',
                  borderRadius: '6px',
                  background: idx === safeIndex ? selectionColor : '#1f1f1f',
                  border: `1px solid ${idx === safeIndex ? selectionColor : '#333'}`,
                  color: idx === safeIndex ? selectionContrastColor : '#fff',
                  fontWeight: '600',
                  fontSize: displayItems.length > 3 ? '12px' : '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {usePercents
                  ? `${(val / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`
                  : <MoneyTitle amount={val} currency={currency} digits={0} />
                }
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', margin: '8px 0 16px', color: '#fff', fontSize: '13px', cursor: 'pointer' }}>
            Custom amount <EditOutlined style={{ fontSize: '11px' }} />
          </div>

          {visibleFields.map((field, idx) => {
            const meta = getFieldMeta(field);
            const requirementLabel = field?.requirement === 'optional' ? '(optional)' : '';
            return (
            <div key={`field-${idx}`} style={{ marginTop: idx === 0 ? '8px' : '16px' }}>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginBottom: '8px' }}>
                {meta.label} {requirementLabel}
              </div>
              <div style={{ 
                background: '#141414', 
                border: '1px solid #333', 
                borderRadius: '8px', 
                padding: '12px',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '13px'
              }}>
                {meta.placeholder}
              </div>
            </div>
          )})}

          {showFeesToggle && (
            <div style={{ marginTop: '18px', background: '#1a1a1a', borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: '600' }}>Include fees in tip</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                  <MoneyTitle amount={feeTotal} currency={currency} digits={2} />
                </div>
              </div>
              <div style={{
                width: '44px',
                height: '26px',
                borderRadius: '999px',
                background: includeFeesByDefault ? '#2cad5a' : '#333',
                position: 'relative'
              }}>
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: '2px',
                  left: includeFeesByDefault ? '20px' : '2px',
                  transition: 'left 0.2s ease'
                }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TipPagePreview;
